import { SEED_DEPLOYMENTS, SEED_PROFILES } from './seeds';
import type {
  BusinessProfileRecord,
  DeployFormValues,
  DeploymentRecord,
} from './types';

interface Store {
  profiles: BusinessProfileRecord[];
  deployments: DeploymentRecord[];
}

const STORAGE_KEY = 'petota-business:admin-profiles-mock';

function readStore(): Store {
  if (typeof window === 'undefined') {
    return { profiles: SEED_PROFILES, deployments: SEED_DEPLOYMENTS };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const seeded: Store = {
        profiles: SEED_PROFILES,
        deployments: SEED_DEPLOYMENTS,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
      return seeded;
    }
    return JSON.parse(raw) as Store;
  } catch {
    return { profiles: SEED_PROFILES, deployments: SEED_DEPLOYMENTS };
  }
}

function writeStore(store: Store): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function listProfiles(): Promise<BusinessProfileRecord[]> {
  await delay(200);
  return readStore().profiles;
}

export async function getProfile(
  subVerticalKey: string,
): Promise<BusinessProfileRecord | null> {
  await delay(180);
  const { profiles } = readStore();
  return (
    profiles.find(
      (p) => p.subVerticalKey === subVerticalKey && p.status === 'active',
    ) ?? null
  );
}

export async function listDeployments(
  subVerticalKey: string,
): Promise<DeploymentRecord[]> {
  await delay(200);
  return readStore()
    .deployments.filter((d) => d.subVerticalKey === subVerticalKey)
    .sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
}

export async function updateProfile(
  subVerticalKey: string,
  patch: Partial<BusinessProfileRecord['profile']>,
): Promise<BusinessProfileRecord> {
  await delay(260);
  const store = readStore();
  const idx = store.profiles.findIndex(
    (p) => p.subVerticalKey === subVerticalKey && p.status === 'active',
  );
  if (idx < 0) {
    throw new Error(`Profile not found: ${subVerticalKey}`);
  }
  const existing = store.profiles[idx];
  if (!existing) {
    throw new Error(`Profile not found: ${subVerticalKey}`);
  }
  const next: BusinessProfileRecord = {
    ...existing,
    profile: { ...existing.profile, ...patch },
  };
  store.profiles[idx] = next;
  writeStore(store);
  return next;
}

export async function deployProfile(
  subVerticalKey: string,
  values: DeployFormValues,
  actor: { id: string; name: string },
): Promise<DeploymentRecord> {
  await delay(400);
  const store = readStore();
  const target = store.profiles.find(
    (p) => p.subVerticalKey === subVerticalKey && p.status === 'active',
  );
  if (!target) {
    throw new Error(`Profile not found: ${subVerticalKey}`);
  }

  const record: DeploymentRecord = {
    id: uid('dep'),
    profileId: target.id,
    subVerticalKey: target.subVerticalKey,
    profileVersion: target.profileVersion,
    deploymentMode: values.mode,
    tenantScope: values.tenantScope,
    ...(values.tenantScope === 'percentage' && values.percentage !== undefined
      ? { percentage: values.percentage }
      : {}),
    ...(values.tenantScope === 'tenant_list' && values.tenantIds
      ? {
          tenantIds: values.tenantIds
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean),
        }
      : {}),
    reason: values.reason,
    actorId: actor.id,
    actorName: actor.name,
    correlationId: uid('corr'),
    createdAt: new Date().toISOString(),
  };

  store.deployments.unshift(record);
  writeStore(store);
  return record;
}
